import React, { useState } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  calculateCashLandedCost,
  isRegistrantSeller,
  isWarrantyEligible,
  getWarrantyCost,
  ROAD_HAZARD_TIRE_COST,
  VEHICLE_REPLACEMENT_COST,
  VEHICLE_REPLACEMENT_ENABLED,
  formatCAD,
} from '../../containers/PaymentCalculator/calculationEngine';

import css from './CashTab.module.css';

export default function CashTab({ vehiclePrice, province, sellerType, vehicleKm, inServiceDate }) {
  const intl = useIntl();
  const [postalCode, setPostalCode] = useState('');
  const [deliveryEstimate, setDeliveryEstimate] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');
  const [warrantyOn, setWarrantyOn] = useState(false);
  const [tireOn, setTireOn] = useState(false);
  const [replacementOn, setReplacementOn] = useState(false);

  const warrantyEligible = isWarrantyEligible(inServiceDate, vehicleKm);
  const warrantyCost = getWarrantyCost(vehiclePrice);

  const landedCost = calculateCashLandedCost({
    vehiclePrice,
    province,
    sellerType,
    deliveryEstimate: deliveryEstimate?.low ?? 0,
  });

  const fetchDeliveryEstimate = async () => {
    if (!postalCode.trim()) return;
    setDeliveryLoading(true);
    setDeliveryError('');
    try {
      const params = new URLSearchParams({
        sellerCode: province || '',
        sellerCountry: 'ca',
        buyerCode: postalCode.trim(),
        buyerCountry: 'ca',
      });
      const res = await window.fetch(`/api/delivery-estimate?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setDeliveryEstimate(data.result);
      } else {
        setDeliveryError(data.error || intl.formatMessage({ id: 'CashTab.deliveryError' }));
      }
    } catch {
      setDeliveryError(intl.formatMessage({ id: 'CashTab.deliveryError' }));
    } finally {
      setDeliveryLoading(false);
    }
  };

  return (
    <div className={css.root}>
      <div className={css.summary}>
        <div className={css.summaryRow}>
          <span>
            <FormattedMessage id="CashTab.vehiclePriceLabel" />
          </span>
          <span>{formatCAD(vehiclePrice)}</span>
        </div>
        <div className={css.summaryRow}>
          <span>
            <FormattedMessage
              id={
                isRegistrantSeller(sellerType)
                  ? 'CashTab.taxLabelRegistrant'
                  : 'CashTab.taxLabelPrivate'
              }
            />
          </span>
          <span>{formatCAD(landedCost.tax)}</span>
        </div>

        <div className={css.deliveryRow}>
          <label className={css.postalLabel}>
            <FormattedMessage id="CashTab.postalCodeLabel" />
            <input
              type="text"
              className={css.postalInput}
              value={postalCode}
              onChange={e => setPostalCode(e.target.value)}
              placeholder={intl.formatMessage({ id: 'CashTab.postalCodePlaceholder' })}
            />
          </label>
          <button
            type="button"
            className={css.deliveryButton}
            onClick={fetchDeliveryEstimate}
            disabled={deliveryLoading || !postalCode.trim()}
          >
            <FormattedMessage id="CashTab.getDeliveryEstimate" />
          </button>
        </div>
        {deliveryError ? <div className={css.error}>{deliveryError}</div> : null}

        <div className={css.summaryRow}>
          <span>
            <FormattedMessage id="CashTab.deliveryEstimateLabel" />
          </span>
          <span>
            {deliveryEstimate ? (
              `${formatCAD(deliveryEstimate.low)} – ${formatCAD(deliveryEstimate.high)}`
            ) : (
              <FormattedMessage id="CashTab.deliveryNotEstimated" />
            )}
          </span>
        </div>

        <div className={css.summaryRow}>
          <span>
            <FormattedMessage id="CashTab.registrationEstimateLabel" />
          </span>
          <span>{formatCAD(landedCost.registrationEstimate)}</span>
        </div>

        <div className={`${css.summaryRow} ${css.summaryRowTotal}`}>
          <span>
            <FormattedMessage id="CashTab.totalLandedCostLabel" />
          </span>
          <span>{formatCAD(landedCost.total)}</span>
        </div>
      </div>

      <p className={css.addOnsIntro}>
        <FormattedMessage id="CashTab.addOnsIntro" />
      </p>
      <div className={css.protections}>
        {warrantyEligible ? (
          <label className={css.protectionRow}>
            <span>
              <input
                type="checkbox"
                checked={warrantyOn}
                onChange={e => setWarrantyOn(e.target.checked)}
              />{' '}
              <FormattedMessage id="FinanceTab.warranty" />
            </span>
            <span>{formatCAD(warrantyCost)}</span>
          </label>
        ) : null}
        <label className={css.protectionRow}>
          <span>
            <input type="checkbox" checked={tireOn} onChange={e => setTireOn(e.target.checked)} />{' '}
            <FormattedMessage id="FinanceTab.roadHazardTire" />
          </span>
          <span>{formatCAD(ROAD_HAZARD_TIRE_COST)}</span>
        </label>
        {VEHICLE_REPLACEMENT_ENABLED ? (
          <label className={css.protectionRow}>
            <span>
              <input
                type="checkbox"
                checked={replacementOn}
                onChange={e => setReplacementOn(e.target.checked)}
              />{' '}
              <FormattedMessage id="FinanceTab.vehicleReplacement" />
            </span>
            <span>{formatCAD(VEHICLE_REPLACEMENT_COST)}</span>
          </label>
        ) : null}
      </div>
      <p className={css.addOnsNote}>
        <FormattedMessage id="CashTab.addOnsNote" />
      </p>
    </div>
  );
}
